import { Flex, Box, Text } from '@chakra-ui/react';
import { Composition } from '@texas/api/endpoints/compositionApi';
import {
  MetadataList,
  MetadataListRow,
} from '@texas/components/shared/MetadataList';
import { NoData } from '@texas/components/shared/NoData';
import { CompositionTitle } from '../CompositionTitle';
import React from 'react';
import { ColorBox } from '@texas/components/shared/colorPicker/ColorBox';
import { metricShortName } from '@texas/components/shared/composition/dimension/translations';
import useFormat from '@texas/hooks/useFormat';

export function Techniques({ compositions }: { compositions: Composition[] }) {
  const anyTechnique = compositions.some((c) =>
    c.materials.some((m) => m.techniques.length > 0),
  );

  return (
    <>
      {!anyTechnique ? (
        <NoData />
      ) : (
        <>
          {compositions.map((x, i) => {
            return (
              <Flex gap={2} key={x.id}>
                {compositions.length > 1 && (
                  <CompositionTitle note={x.note} index={i + 1} />
                )}
                <Box w="full" alignSelf="center">
                  <MetadataList>
                    {x.materials.map((m, i) => {
                      return (
                        <MetadataListRow
                          key={`${m.id}-${i}`}
                          label={m.material.text}
                        >
                          {m.techniques.map((t) => (
                            <Text key={t.id}>{t.text}</Text>
                          ))}
                        </MetadataListRow>
                      );
                    })}
                  </MetadataList>
                </Box>
              </Flex>
            );
          })}
        </>
      )}
    </>
  );
}

export function Colors({ compositions }: { compositions: Composition[] }) {
  const anyColor = compositions.some((c) =>
    c.materials.some((m) => m.colors.length > 0),
  );

  return (
    <>
      {!anyColor ? (
        <NoData />
      ) : (
        <>
          {compositions.map((x, i) => {
            return (
              <Flex gap={2} key={x.id}>
                {compositions.length > 1 && (
                  <CompositionTitle note={x.note} index={i + 1} />
                )}
                <Flex w="full" alignSelf="center" flexDir="column" gap={2}>
                  {x.materials.map((m) => {
                    return (
                      <React.Fragment key={m.id}>
                        {m.colors.map((c) => (
                          <ColorBox
                            key={c.code}
                            color={c}
                            colorName={c.name}
                            colorDescription={c.description}
                          />
                        ))}
                      </React.Fragment>
                    );
                  })}
                </Flex>
              </Flex>
            );
          })}
        </>
      )}
    </>
  );
}

export function Dimensions({ compositions }: { compositions: Composition[] }) {
  const anySize = compositions.some((c) => c.dimensionGroup !== null);

  return (
    <>
      {!anySize ? (
        <NoData />
      ) : (
        <>
          {compositions.map((x, i) => {
            return (
              <Flex gap={2} key={x.id}>
                {compositions.length > 1 && (
                  <CompositionTitle note={x.note} index={i + 1} />
                )}
                {x.dimensionGroup === null && <NoData />}
                <Box w="full" alignSelf="center">
                  <Text pb={2} fontWeight="bold">
                    {x.dimensionGroup?.name}
                  </Text>
                  <MetadataList>
                    {x.dimensionGroup?.dimensions.map((d) => {
                      return (
                        <MetadataListRow key={d.id} label={d.label}>
                          {d.value} {metricShortName(d.metric)}
                        </MetadataListRow>
                      );
                    })}
                  </MetadataList>
                </Box>
              </Flex>
            );
          })}
        </>
      )}
    </>
  );
}

type MaterialMetadataField = 'articleTextField' | 'certificationText';

export function Materials({
  compositions,
  metadataField,
}: {
  compositions: Composition[];
  metadataField: MaterialMetadataField;
}) {
  const anyMaterial = compositions.some((c) => c.materials.length > 0);

  return (
    <>
      {!anyMaterial ? (
        <NoData />
      ) : (
        <>
          {compositions.map((x, i) => {
            return (
              <Flex gap={2} key={x.id}>
                {compositions.length > 1 && (
                  <CompositionTitle note={x.note} index={i + 1} />
                )}
                <Box w="full" alignSelf="center">
                  <MetadataList>
                    {x.materials.map((m, i) => {
                      return (
                        <MetadataListRow
                          key={`${m.id}-${i}`}
                          label={m.material.text}
                        >
                          {metadataField === 'articleTextField' ? (
                            <>{m.material.articleTextField}</>
                          ) : (
                            <>{m.material.certificationText}</>
                          )}
                        </MetadataListRow>
                      );
                    })}
                  </MetadataList>
                </Box>
              </Flex>
            );
          })}
        </>
      )}
    </>
  );
}

export function Weight({ compositions }: { compositions: Composition[] }) {
  const { formatWeight } = useFormat();
  const anyWeight = compositions.some((c) =>
    c.materials.some((m) => m.weight > 0),
  );

  return (
    <>
      {!anyWeight ? (
        <NoData />
      ) : (
        <>
          {' '}
          {compositions.map((x, i) => {
            return (
              <Flex gap={2} key={x.id}>
                {compositions.length > 1 && (
                  <CompositionTitle note={x.note} index={i + 1} />
                )}
                <Box w="full" alignSelf="center">
                  <MetadataList>
                    {x.materials.map((m, i) => {
                      return (
                        <MetadataListRow
                          key={`${m.id}-${i}`}
                          label={m.material.text}
                        >
                          {formatWeight(m.weight)}
                        </MetadataListRow>
                      );
                    })}
                  </MetadataList>
                </Box>
              </Flex>
            );
          })}
        </>
      )}
    </>
  );
}
